import { useEffect, useState } from 'react'
import { Play, Check, Loader2, Monitor, Clock } from 'lucide-react'
import { ExerciseDetail } from '@renderer/types/api'

export type LaunchMode = 'intro' | 'provisioning'

interface LaunchCardProps {
  exercise: ExerciseDetail
  mode: LaunchMode
  onLaunch: () => void
  onBack: () => void
  onProvisioned: () => void
  onConnecting?: () => void
  launchReady?: boolean
  teachingMode: 'guide' | 'assist'
  onTeachingModeChange: (mode: 'guide' | 'assist') => void
}

const MODES: {
  value: 'guide' | 'assist'
  label: string
  blurb: string
}[] = [
  {
    value: 'guide',
    label: 'Guide me step by step',
    blurb: 'The tutor walks you through each action and moves you on when it sees a task is done.'
  },
  {
    value: 'assist',
    label: 'Assist me when I need help',
    blurb: "You work on your own. The tutor stays quiet until you ask, or until something on screen looks wrong."
  }
]

const STAGES = [
  'Reserve environment',
  'Provision jump host',
  'Boot ONTAP cluster3',
  'Network & SSH access',
  'Connect Labs Agent'
]

// Sums to ~7s — feels real without being annoying. Replace with actual
// provisioning events when the backend supports them.
const STAGE_DURATIONS_MS = [800, 2500, 1800, 1200, 900]

const fmtMMSS = (seconds: number) => {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0')
  const s = (seconds % 60).toString().padStart(2, '0')
  return `${m}:${s}`
}

// Pulls the module ref from a task ref_code like "M2.E1.T1.S3" → "M2 · E1"
const formatRefCode = (refCode: string | undefined) => {
  if (!refCode) return ''
  const parts = refCode.split('.')
  return parts.slice(0, 2).join(' · ')
}

export const LaunchCard: React.FC<LaunchCardProps> = ({
  exercise,
  mode,
  onLaunch,
  onBack,
  onProvisioned,
  onConnecting,
  launchReady = true,
  teachingMode,
  onTeachingModeChange,
}) => {
  const [stageIdx, setStageIdx] = useState(0)
  const [elapsed, setElapsed] = useState(0)

  // Reset progression each time provisioning kicks off
  useEffect(() => {
    if (mode === 'provisioning') {
      setStageIdx(0)
      setElapsed(0)
    }
  }, [mode])

  // Kick off the LiveKit connection when the last stage begins
  useEffect(() => {
    if (mode === 'provisioning' && stageIdx === STAGES.length - 1) {
      onConnecting?.()
    }
  }, [mode, stageIdx, onConnecting])

  // Drive stage progression
  useEffect(() => {
    if (mode !== 'provisioning') return
    if (stageIdx >= STAGES.length) {
      const t = setTimeout(onProvisioned, 500)
      return () => clearTimeout(t)
    }
    const t = setTimeout(() => setStageIdx((i) => i + 1), STAGE_DURATIONS_MS[stageIdx])
    return () => clearTimeout(t)
  }, [mode, stageIdx, onProvisioned])

  // Drive elapsed-time clock
  useEffect(() => {
    if (mode !== 'provisioning') return
    const i = setInterval(() => setElapsed((s) => s + 1), 1000)
    return () => clearInterval(i)
  }, [mode])

  const tasks = exercise.tasks || []
  const refDisplay = formatRefCode(exercise.ref_code)

  return (
    <div
      className="w-full h-full overflow-y-auto"
      style={{
        background:
          'radial-gradient(ellipse 60% 55% at 50% 32%, #3c2b12 0%, #27200f 45%, #1c1712 100%)'
      }}
    >
      <style>{`
        @media (max-height: 760px) {
          .lc-desc, .lc-blurb { display: none !important; }
          .lc-outer-pad { padding-top: 12px !important; padding-bottom: 12px !important; }
          .lc-icon { width: 48px !important; height: 48px !important; margin-bottom: 10px !important; }
          .lc-modes { gap: 6px !important; margin-bottom: 14px !important; }
          .lc-mode-card { padding: 8px !important; }
          .lc-stage-row { margin-top: 16px !important; }
        }
        @media (max-height: 620px) {
          .lc-pills { display: none !important; }
          .lc-stage-row { display: none !important; }
        }
      `}</style>
      <div className="min-h-full flex items-center justify-center px-4 sm:px-6 py-6 sm:py-10 lc-outer-pad">
        <div className="w-full max-w-2xl flex flex-col items-center text-center">
          <div
            className="lc-icon rounded-2xl grid place-items-center mb-4 shrink-0"
            style={{
              width: 72,
              height: 72,
              background: 'linear-gradient(145deg, #e0ad4c, #c9922c)',
              boxShadow: '0 0 44px rgba(212,169,74,0.35)'
            }}
          >
            <Monitor className="w-8 h-8" style={{ color: '#2c2200' }} strokeWidth={2} />
          </div>

          <p
            className="text-[11px] font-semibold tracking-[0.18em] uppercase mb-2"
            style={{ color: '#d4a94a' }}
          >
            {refDisplay ? `${refDisplay} · Hands-on Lab` : 'Hands-on Lab'}
          </p>

          <h1
            className="text-2xl sm:text-3xl font-bold mb-2"
            style={{ color: '#f5ecd6', letterSpacing: '-0.01em' }}
          >
            {exercise.title}
          </h1>

          <p
            className="text-[14px] leading-relaxed max-w-lg mb-5 lc-desc"
            style={{ color: '#b3a788' }}
          >
            A streamed Windows jump host with full agent assistance, ready in about a minute.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-2 mb-6 lc-pills">
            <MetaPill icon={<Clock className="w-3.5 h-3.5" />} label="30 mins" />
            <MetaPill icon={<Check className="w-3.5 h-3.5" />} label={`${tasks.length} tasks`} />
            <MetaPill icon={<Monitor className="w-3.5 h-3.5" />} label="Windows + ONTAP 9.14" />
          </div>

          <div className="w-full grid sm:grid-cols-2 gap-3 mb-6 lc-modes">
            {MODES.map((m) => {
              const selected = teachingMode === m.value
              return (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => onTeachingModeChange(m.value)}
                  disabled={mode === 'provisioning'}
                  aria-pressed={selected}
                  className="lc-mode-card text-left rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed p-3.5"
                  style={{
                    background: selected ? 'rgba(212,169,74,0.08)' : '#1a1510',
                    border: selected ? '1.5px solid #d4a94a' : '1.5px solid #3a2f1a',
                    boxShadow: selected ? '0 0 26px rgba(212,169,74,0.15)' : 'none'
                  }}
                >
                  <p className="text-[14px] font-semibold mb-1" style={{ color: '#f5ecd6' }}>
                    {m.label}
                  </p>
                  <p className="text-[12px] leading-snug lc-blurb" style={{ color: '#a89c7e' }}>
                    {m.blurb}
                  </p>
                </button>
              )
            })}
          </div>

          <button
            onClick={() => {
              navigator.clipboard?.readText?.().catch(() => {})
              onLaunch()
            }}
            disabled={mode === 'provisioning' || !launchReady}
            className="w-full inline-flex items-center justify-center gap-2 h-12 sm:h-14 text-[15px] font-semibold rounded-full transition-all disabled:cursor-not-allowed"
            style={{
              color: mode === 'provisioning' || !launchReady ? '#8a7f5e' : '#2c2200',
              background: mode === 'provisioning' || !launchReady ? '#3a2f1a' : 'linear-gradient(145deg, #e0ad4c, #c9922c)',
              boxShadow:
                mode === 'provisioning' || !launchReady ? 'none' : '0 0 40px rgba(212,169,74,0.3)'
            }}
          >
            {mode === 'provisioning' ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Setting up your environment…
              </>
            ) : !launchReady ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading…
              </>
            ) : (
              <>
                <Play className="w-4 h-4 fill-current" />
                Launch Lab Setup
              </>
            )}
          </button>

          <button
            onClick={onBack}
            disabled={mode === 'provisioning'}
            className="mt-3 text-[13px] font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ color: '#8a816c', background: 'transparent' }}
          >
            Back to exercise
          </button>

          {mode === 'provisioning' && (
            <p
              className="text-[11px] font-semibold tracking-[0.14em] uppercase mt-8 mb-3"
              style={{ color: '#d4a94a' }}
            >
              Setting up your lab — {fmtMMSS(elapsed)}
            </p>
          )}
          <div
            className={`w-full flex items-start justify-between gap-2 lc-stage-row ${mode === 'provisioning' ? '' : 'mt-8'}`}
          >
            {STAGES.map((label, idx) => {
              const status =
                mode === 'intro'
                  ? 'done'
                  : idx < stageIdx
                    ? 'done'
                    : idx === stageIdx
                      ? 'working'
                      : 'pending'
              return <StageDot key={label} label={label} status={status} />
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

const MetaPill: React.FC<{ icon: React.ReactNode; label: string }> = ({ icon, label }) => (
  <div
    className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full border text-[12px] font-medium"
    style={{ background: '#1a1510', borderColor: '#3a2f1a', color: '#d8cdb0' }}
  >
    <span style={{ color: '#d4a94a' }}>{icon}</span>
    {label}
  </div>
)

const StageDot: React.FC<{ label: string; status: 'pending' | 'working' | 'done' }> = ({
  label,
  status
}) => (
  <div className="flex-1 min-w-0 flex flex-col items-center gap-2">
    <div
      className="w-2.5 h-2.5 rounded-full shrink-0"
      style={{
        background: status === 'pending' ? '#4a3f2a' : '#d4a94a',
        boxShadow: status === 'working' ? '0 0 0 4px rgba(212,169,74,0.18)' : 'none'
      }}
    />
    <span
      className="text-[11px] leading-snug text-center"
      style={{ color: status === 'pending' ? '#6b6354' : '#b3a788' }}
    >
      {label}
    </span>
  </div>
)
